import { EventEmitter } from '~common/utils/events'
import type { Services, ServiceStatePersisters } from '~/services/types'
import type {
    Storage,
    Invoice,
    BankTransaction,
    DocumentTransactionAssociation,
    UserBankAccount,
} from '~/storage/types'
import { UserSettings } from '~common/storage/users'
import { FirebaseAuthState } from '~common/google-auth/services/types'
import { CachedData } from './types'
import {
    deserializeQueryStringToSearchState,
    updateQueryStringWithSearchState,
} from './utils/searchstate-querystring'
import { Email } from '~common/storage/email'
import { FirebaseError } from 'firebase/app'

export interface Dependencies {
    storage: Storage
    getServices: () => Pick<Services, 'auth' | 'googleApi' | 'search'>
    statePersisters: ServiceStatePersisters
}

export class CacheService {
    public _data: CachedData = {
        loadState: 'pristine',
        authState: {
            user: null,
            data: null,
            error: null,
        },
        settings: null,
        entitlement: null,
        offeredPricing: null,
        emails: {},
        invoices: {},
        amountRange: undefined,
        emailInvoices: {},
        vendors: {},
        importProgress: null,
        stats: {
            invoicesCount: 0,
        },
        searchState: null,
        transactions: {},
        // New keys for matching associations
        matchesByTransaction: {},
        matchesByInvoice: {},
        documentTransactionAssociations: {},
        bankAccounts: {},
        categoryMatching: {},
        activeGmailAccount: null,
    }

    onUpdate = new EventEmitter<Set<keyof CachedData>>()
    // Fine-grained update channel for emails: emits the set of changed email IDs
    onEmailsUpdate = new EventEmitter<Set<string>>()
    // Fine-grained update channel for invoices: emits the set of changed invoice IDs
    onInvoicesUpdate = new EventEmitter<Set<string>>()
    // Fine-grained update channel for email->invoice mapping by email id
    onEmailInvoicesUpdate = new EventEmitter<Set<string>>()
    // Emits when the set of email IDs changes (add/remove)
    onEmailIdsUpdate = new EventEmitter<Set<string>>()
    onTransactionsUpdate = new EventEmitter<Set<string>>()
    // New emitter for matching associations updates
    onMatchingAssociationsUpdate = new EventEmitter<Set<string>>()
    // New fine-grained emitters for affected transactions/invoices from associations
    onTransactionsWithAssociationChanges = new EventEmitter<Set<string>>()
    onInvoicesWithAssociationChanges = new EventEmitter<Set<string>>()

    // Internal membership tracking
    private emailIdSet: Set<string> = new Set()
    private emailIds: string[] = []
    private emailsUpdateVersion = 0

    private unsubscribeFromAuthChanges: (() => void) | null = null
    private tokenRefreshTimer: NodeJS.Timeout | null = null
    private unsubscribeFromAssociations: (() => void) | null = null

    constructor(private deps: Dependencies) {
        this.initialize() // TODO: Maybe invoke this somewhere else
    }

    async setActiveGmailAccount(email: string | null) {
        this.setKey('activeGmailAccount', email)
        const services = this.deps.getServices()
        services.googleApi.setActiveEmail(email)

        // When switching active account, we need to update the Google API access token
        // to match the selected account.
        if (email) {
            try {
                const { accessToken } =
                    await services.auth.refreshAuthToken(email)
                services.googleApi.setToken(email, accessToken)
            } catch (error) {
                console.error(
                    `Failed to refresh token for active account ${email}:`,
                    error,
                )
            }
        }
    }

    async initialize() {
        this.setKey('loadState', 'running')
        try {
            await Promise.allSettled([
                await this.initializeAuth(),
                await this.loadInvoicesFromImportProgress(),
            ])
            if (this.getKey('authState').user) {
                await Promise.allSettled([
                    await this.loadAllInvoices(),
                    await this.loadAllTransactions(),
                    await this.loadAllBankAccounts(),
                    await this.loadAllMatchingAssociations(),
                    await this.loadRecentEmails(),
                ])
                // Realtime listeners are attached from index.ts
            }
            // Load persisted searchState into cache
            // First try to load from query string, then fall back to localStorage
            const queryStringSearchState = deserializeQueryStringToSearchState()
            if (queryStringSearchState) {
                this.setKey('searchState', queryStringSearchState)
            } else {
                // TODO: Remove direct usage of localStorage
                const savedStr = localStorage.getItem('searchState')
                if (savedStr) {
                    try {
                        this.setKey('searchState', JSON.parse(savedStr))
                    } catch (e) {
                        console.warn('Failed to load searchState from cache', e)
                    }
                }
            }
            this.setupAuthStateListener()
            this.setKey('loadState', 'success')
        } catch (error) {
            this.setKey('loadState', 'error')
        }
    }

    async cleanup(options?: { keepAuthListener?: boolean }) {
        if (!options?.keepAuthListener) {
            if (this.unsubscribeFromAuthChanges) {
                this.unsubscribeFromAuthChanges()
                this.unsubscribeFromAuthChanges = null
            }
        }

        if (this.unsubscribeFromAssociations) {
            this.unsubscribeFromAssociations()
            this.unsubscribeFromAssociations = null
        }

        if (this.tokenRefreshTimer) {
            clearTimeout(this.tokenRefreshTimer)
            this.tokenRefreshTimer = null
        }

        // Realtime invoice/transaction listeners are managed in index.ts
    }

    private async loadAllInvoices(): Promise<void> {
        const storageModules = this.deps.storage.modules
        const invoices = await storageModules.emailInvoice.getAllInvoices()

        if (invoices == null || invoices.length === 0) {
            return
        }

        const cacheUpdates: Record<string, Invoice> = {}
        for (const invoice of invoices) {
            cacheUpdates[invoice.id] = invoice
        }
        const emailInvoicesUpdates: Record<string, string[]> = {
            ...this._data.emailInvoices,
        }
        for (const invoice of Object.values(invoices)) {
            if (invoice.sourceEmailId) {
                if (!emailInvoicesUpdates[invoice.sourceEmailId]) {
                    emailInvoicesUpdates[invoice.sourceEmailId] = []
                }
                emailInvoicesUpdates[invoice.sourceEmailId].push(invoice.id)
            }
        }

        // Update cache with invoices and emailInvoices mapping in one go
        this.setKeys({
            invoices: { ...this._data.invoices, ...cacheUpdates },
            emailInvoices: emailInvoicesUpdates,
            stats: {
                ...this._data.stats,
                invoicesCount: Object.keys({
                    ...this._data.invoices,
                    ...cacheUpdates,
                }).length,
            },
        })
    }

    private async loadAllTransactions(): Promise<void> {
        const userId = this.getKey('authState').user?.uid
        if (!userId) return

        const storageModules = this.deps.storage.modules
        const transactions =
            await storageModules.bankTransaction.getAllTransactions()
        if (transactions.length === 0) return

        const cacheUpdates: Record<string, BankTransaction> = {}
        for (const transaction of transactions) {
            cacheUpdates[transaction.id] = transaction
        }

        this.setKeys({
            transactions: {
                ...(this._data.transactions ?? {}),
                ...cacheUpdates,
            },
        })

        if (Object.keys(cacheUpdates).length > 0) {
            this.onTransactionsUpdate.emit(new Set(Object.keys(cacheUpdates)))
        }
    }
    private async loadAllBankAccounts(): Promise<void> {
        const userId = this.getKey('authState').user?.uid
        if (!userId) return

        const storageModules = this.deps.storage.modules
        const bankAccounts =
            await storageModules.bankTransaction.getAllBankAccounts()
        const bankAccountsRecord: Record<string, UserBankAccount> = {}
        for (const account of bankAccounts) {
            bankAccountsRecord[account.iban] = account
        }
        this.setKeys({
            bankAccounts: bankAccountsRecord,
        })
    }

    private async loadRecentEmails(
        hasInvoicesOnly: boolean = true,
    ): Promise<void> {
        const emails =
            await this.deps.storage.modules.emailInvoice.getRecentEmails({
                limitCount: 50,
                hasInvoicesOnly,
            })
        // Use fine-grained setter to maintain membership and emit per-id updates
        await this.setEmail(emails as Email[])
    }

    private async loadInvoicesFromImportProgress(): Promise<void> {
        const emailImporterState =
            await this.deps.statePersisters.importer.hydrate()
        this.setKey('importProgress', emailImporterState)
    }

    private async loadAllMatchingAssociations(): Promise<void> {
        const userId = this.getKey('authState').user?.uid
        if (!userId) return

        const storageModules = this.deps.storage.modules
        // Assume getAllAssociations(userId) returns DocumentTransactionAssociation[]
        const associations =
            await storageModules.matchingAssociations.getAllAssociations()

        if (associations.length === 0) return

        this.updateAssociationsFromArray(associations)
    }

    private updateAssociationsFromArray(
        associations: DocumentTransactionAssociation[],
    ) {
        const matchesByTransaction: Record<string, string[]> = {}
        const matchesByInvoice: Record<string, string[]> = {}
        const documentTransactionAssociations: Record<
            string,
            DocumentTransactionAssociation
        > = {}

        for (const assoc of associations) {
            documentTransactionAssociations[assoc.id] = assoc

            if (!matchesByTransaction[assoc.transactionId]) {
                matchesByTransaction[assoc.transactionId] = []
            }
            matchesByTransaction[assoc.transactionId].push(assoc.id)

            if (!matchesByInvoice[assoc.documentId]) {
                matchesByInvoice[assoc.documentId] = []
            }
            matchesByInvoice[assoc.documentId].push(assoc.id)
        }

        this.setKeys({
            matchesByTransaction,
            matchesByInvoice,
            documentTransactionAssociations,
        })

        if (associations.length > 0) {
            this.onMatchingAssociationsUpdate.emit(
                new Set(associations.map((a) => a.id)),
            )
            this.onUpdate.emit(
                new Set([
                    'matchesByTransaction',
                    'matchesByInvoice',
                    'documentTransactionAssociations',
                ]),
            )
        }
    }

    clearEmails(): void {
        const previousIds = new Set(Object.keys(this._data.emails))
        this.setKey('emails', {})
        // Reset membership tracking and emit membership change
        if (previousIds.size > 0) {
            this.emailsUpdateVersion++
            this.onEmailsUpdate.emit(previousIds)
            this.onEmailIdsUpdate.emit(previousIds)
            this.emailIdSet.clear()
            this.emailIds = []
        }
    }

    deleteEmails(emailIds: string[]): void {
        const updatedEmails = { ...this._data.emails }
        for (const id of emailIds) {
            delete updatedEmails[id]
        }
        this.setKeys({ emails: updatedEmails })
        if (emailIds.length > 0) {
            // Update membership tracking and emit membership change if needed
            let membershipChanged = false
            for (const id of emailIds) {
                if (this.emailIdSet.delete(id)) {
                    membershipChanged = true
                }
            }
            if (membershipChanged) {
                this.emailIds = Array.from(this.emailIdSet)
                this.onEmailIdsUpdate.emit(new Set(emailIds))
            }
            this.emailsUpdateVersion++
            this.onEmailsUpdate.emit(new Set(emailIds))
        }
    }

    /**
     * Replace an email with a new one, maintaining its position in the ordered list.
     * This is useful when replacing a temporary email with a real one.
     */
    async replaceEmail(oldEmailId: string, newEmail: Email): Promise<Email> {
        const currentEmails = this._data.emails
        const oldEmail = currentEmails[oldEmailId]
        if (!oldEmail) {
            throw new Error(`Email with id ${oldEmailId} not found`)
        }

        const positionIndex = this.emailIds.indexOf(oldEmailId)
        const wasAtPosition = positionIndex !== -1

        const updatedEmails = { ...currentEmails }
        delete updatedEmails[oldEmailId]
        updatedEmails[newEmail.id] = newEmail

        this._data.emails = updatedEmails

        this.emailIdSet.delete(oldEmailId)
        this.emailIdSet.add(newEmail.id)

        if (wasAtPosition) {
            this.emailIds.splice(positionIndex, 1, newEmail.id)
        } else {
            this.emailIds = Array.from(this.emailIdSet)
        }

        this.onEmailIdsUpdate.emit(new Set([oldEmailId, newEmail.id]))
        this.onUpdate.emit(new Set(['emails']))
        this.emailsUpdateVersion++
        this.onEmailsUpdate.emit(new Set([oldEmailId, newEmail.id]))

        return newEmail
    }

    private async initializeAuth(): Promise<void> {
        const services = this.deps.getServices()
        const user = await services.auth.getLoggedInUser()
        if (!user) {
            this.setKey('authState', {
                user,
                data: null,
                error: 'no user logged in',
            })
            return
        }

        const storageModules = this.deps.storage.modules

        let userDoc
        try {
            userDoc = await storageModules.auth.getUser(user.uid)
        } catch (error) {
            if (
                error instanceof FirebaseError &&
                error.code === 'permission-denied'
            ) {
                console.error(
                    'Permission denied on getUser, likely auth race. Retrying...',
                    error.code,
                )
                // Re-await to ensure auth is fully ready
                const refreshedUser = await services.auth.getLoggedInUser()
                if (!refreshedUser) {
                    throw new Error('User disappeared during retry')
                }
                // Optional: Force token refresh
                await refreshedUser.getIdToken(true)
                // Retry after short delay
                await new Promise((resolve) => setTimeout(resolve, 3000))
                userDoc = await storageModules.auth.getUser(refreshedUser.uid)
            } else {
                throw error
            }
        }

        if (!userDoc) {
            this.setKey('authState', { user, data: null, error: null })
            return
        }

        // Only refresh Google tokens if the user has Google accounts
        // Email/password users don't have Google OAuth tokens
        const expiresAt = await this.refreshAllGoogleAccountTokens(
            user.uid,
            services,
            storageModules,
        )
        const entitlement = await storageModules.entitlements.getEntitlement(
            user.uid,
        )
        if (!entitlement) {
            this.setKey('entitlement', null)
        } else {
            this.setKey('entitlement', entitlement)
        }

        this.setKey('authState', { user, data: userDoc, error: null })
        this.setKey('settings', userDoc.settings ?? null)

        if (expiresAt != null) {
            await this.scheduleNextTokenRefresh(expiresAt)
        }
    }

    private setupAuthStateListener(): void {
        const services = this.deps.getServices()
        // Set up auth state change listener
        this.unsubscribeFromAuthChanges = services.auth.onAuthStateChanged(
            (authState) => {
                const previousUserId = this.getKey('authState').user?.uid
                this.setKey('authState', {
                    ...this.getKey('authState'),
                    ...authState,
                })

                const newUserId = authState.user?.uid
                if (newUserId !== previousUserId) {
                    // Only clear cache data and association listeners; realtime doc listeners are managed in index.ts
                    this.cleanup({ keepAuthListener: true })

                    if (newUserId) {
                        // Do not load or attach listeners here
                    } else {
                        // Clear data on logout
                        this.setKeys({
                            matchesByTransaction: {},
                            matchesByInvoice: {},
                            documentTransactionAssociations: {},
                            invoices: {},
                            transactions: {},
                            bankAccounts: {},
                        })
                    }
                }
            },
        )
    }

    private async refreshAllGoogleAccountTokens(
        userId: string,
        services: Pick<Services, 'auth' | 'googleApi'>,
        storageModules: Storage['modules'],
    ): Promise<number | null> {
        const hasGoogleAccounts =
            await storageModules.auth.hasGoogleAccounts(userId)
        if (!hasGoogleAccounts) {
            return null
        }

        try {
            const googleAccountEmails =
                await storageModules.auth.getAllGoogleAccountEmails(userId)
            if (googleAccountEmails.length === 0) {
                return null
            }

            let earliestExpiresAt: number | null = null

            for (let i = 0; i < googleAccountEmails.length; i++) {
                const googleAccountEmail = googleAccountEmails[i]
                try {
                    const { accessToken, expiresAt } =
                        await services.auth.refreshAuthToken(googleAccountEmail)

                    services.googleApi.setToken(googleAccountEmail, accessToken)

                    // Always initialize the primary token with the first account
                    if (i === 0) {
                        services.googleApi.initializePrimary(accessToken)
                    }

                    // Set active email if none set or it matches
                    const activeAccount = this.getKey('activeGmailAccount')
                    if (
                        activeAccount === googleAccountEmail ||
                        (!activeAccount && i === 0)
                    ) {
                        services.googleApi.setActiveEmail(googleAccountEmail)
                    }

                    if (expiresAt != null) {
                        if (
                            earliestExpiresAt === null ||
                            expiresAt < earliestExpiresAt
                        ) {
                            earliestExpiresAt = expiresAt
                        }
                    }
                } catch (error) {
                    console.error(
                        `Failed to refresh token for ${googleAccountEmail}:`,
                        error,
                    )
                }
            }

            return earliestExpiresAt
        } catch (error) {
            console.error('Failed to refresh Google auth tokens:', error)
            return null
        }
    }

    private async scheduleNextTokenRefresh(expiresAt: number): Promise<void> {
        if (this.tokenRefreshTimer) {
            clearTimeout(this.tokenRefreshTimer)
            this.tokenRefreshTimer = null
        }
        const services = this.deps.getServices()
        const storageModules = this.deps.storage.modules
        const now = Date.now()
        const timeUntilExpiry = expiresAt - now

        const refreshAndReschedule = async () => {
            const user = await services.auth.getLoggedInUser()
            if (!user) {
                return
            }

            try {
                const newExpiresAt = await this.refreshAllGoogleAccountTokens(
                    user.uid,
                    services,
                    storageModules,
                )

                if (newExpiresAt != null) {
                    await this.scheduleNextTokenRefresh(newExpiresAt)
                }
            } catch (error) {
                console.error('Failed to refresh Google auth tokens:', error)
                // Try again in 1 minute if it fails (could be network)
                this.tokenRefreshTimer = setTimeout(
                    () => this.scheduleNextTokenRefresh(expiresAt),
                    60 * 1000,
                )
            }
        }

        // If the token is already expired, refresh it immediately
        if (timeUntilExpiry <= 0) {
            await refreshAndReschedule()
            return
        }

        // Refresh token 5 minutes before expiry to ensure we don't run out
        const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 1000)
        this.tokenRefreshTimer = setTimeout(refreshAndReschedule, refreshTime)
    }

    async refreshEntitlement(): Promise<void> {
        const userId = this.getKey('authState').user?.uid
        if (!userId) {
            return
        }
        const entitlement =
            await this.deps.storage.modules.entitlements.getEntitlement(userId)
        if (!entitlement) {
            console.warn(
                'Entitlement document not found for authorized user - defaulting to free plan',
            )
            this.setKey('entitlement', null)
        } else {
            this.setKey('entitlement', entitlement)
        }
    }

    getData() {
        // stupid, but just so _data signals that you should only modify it through setters
        return this._data
    }

    getKey<Key extends keyof CachedData>(key: Key) {
        return this._data[key]
    }

    getKeys<K extends readonly (keyof CachedData)[]>(
        keys: K,
    ): {
        [I in keyof K]: K[I] extends keyof CachedData ? CachedData[K[I]] : never
    } {
        return keys.map((key) => this._data[key]) as never
    }

    async getSettings(): Promise<UserSettings | null> {
        let settings = this._data.settings
        if (!settings) {
            const userId = this.getKey('authState').user?.uid
            if (!userId) {
                return null
            }
            settings =
                (await this.deps.storage.modules.auth.getUser(userId))
                    ?.settings ?? null
            this.setKey('settings', settings ?? null)
        }
        return settings ?? null
    }

    setKey<Key extends keyof CachedData>(key: Key, value: CachedData[Key]) {
        this._data[key] = value
        const keys = new Set([key])
        this.onUpdate.emit(keys)
    }

    setKeys(updates: Partial<CachedData>) {
        Object.assign(this._data, updates)
        const keys = new Set(Object.keys(updates) as Array<keyof CachedData>)
        this.onUpdate.emit(keys)
    }

    async getEmailsByIds(params: {
        internalIds?: string[]
        externalIds?: string[]
    }): Promise<Record<string, Email>> {
        const internalIds = params.internalIds ?? []
        const externalIds = params.externalIds ?? []

        if (internalIds.length === 0 && externalIds.length === 0) {
            return {}
        }

        // First, try to get from cache
        const cachedEmails: Record<string, Email> = {}
        const missingInternalIds: string[] = []
        const missingExternalIds: string[] = []

        // Check cache for internal IDs
        for (const id of internalIds) {
            const cached = this._data.emails[id]
            if (cached) {
                cachedEmails[id] = cached
            } else {
                missingInternalIds.push(id)
            }
        }

        // Check cache for external IDs (need to search through all cached emails)
        for (const externalId of externalIds) {
            const foundByExternalId = Object.values(this._data.emails).find(
                (email) => email.externalId === externalId,
            )
            if (foundByExternalId) {
                cachedEmails[externalId] = foundByExternalId
            } else {
                missingExternalIds.push(externalId)
            }
        }

        // If all emails are in cache, return them
        if (
            missingInternalIds.length === 0 &&
            missingExternalIds.length === 0
        ) {
            return cachedEmails
        }

        // Load missing emails from database
        const storageModules = this.deps.storage.modules
        const dbEmails: Record<string, Email> = {}

        // Load internal IDs
        if (missingInternalIds.length > 0) {
            const internalEmails =
                await storageModules.emailInvoice.getEmailsByIds(
                    missingInternalIds,
                )
            Object.assign(dbEmails, internalEmails)
        }

        // Load external IDs
        if (missingExternalIds.length > 0) {
            const externalEmails =
                await storageModules.emailInvoice.getEmailsByExternalIds(
                    missingExternalIds,
                )
            Object.assign(dbEmails, externalEmails)
        }

        // Update cache using fine-grained setter to emit correct events
        await this.setEmail(Object.values(dbEmails) as Email[])

        // Return combined results
        return { ...cachedEmails, ...dbEmails }
    }

    async getInvoicesByIds(ids: string[]): Promise<Record<string, Invoice>> {
        if (ids.length === 0) {
            return {}
        }

        // First, try to get from cache
        const cachedInvoices: Record<string, Invoice> = {}
        const missingIds: string[] = []

        for (const id of ids) {
            const cached = this._data.invoices[id]
            if (cached) {
                cachedInvoices[id] = cached
            } else {
                missingIds.push(id)
            }
        }

        // If all invoices are in cache, return them
        if (missingIds.length === 0) {
            return cachedInvoices
        }

        // Load missing invoices from database
        const storageModules = this.deps.storage.modules
        const dbInvoices =
            await storageModules.emailInvoice.getInvoicesByIds(missingIds)

        // Update cache with new invoices
        const cacheUpdates: Record<string, Invoice> = {}
        for (const [id, invoice] of Object.entries(dbInvoices)) {
            cacheUpdates[id] = invoice
        }

        // Build emailInvoices mapping for new invoices
        const emailInvoicesUpdates: Record<string, string[]> = {
            ...this._data.emailInvoices,
        }
        for (const invoice of Object.values(dbInvoices)) {
            if (invoice.sourceEmailId) {
                if (!emailInvoicesUpdates[invoice.sourceEmailId]) {
                    emailInvoicesUpdates[invoice.sourceEmailId] = []
                }
                emailInvoicesUpdates[invoice.sourceEmailId].push(invoice.id)
            }
        }

        // Update cache with invoices and emailInvoices mapping in one go
        this.setKeys({
            invoices: { ...this._data.invoices, ...cacheUpdates },
            emailInvoices: emailInvoicesUpdates,
            stats: {
                ...this._data.stats,
                invoicesCount: Object.keys({
                    ...this._data.invoices,
                    ...cacheUpdates,
                }).length,
            },
        })

        if (Object.keys(dbInvoices).length > 0) {
            this.onInvoicesUpdate.emit(new Set(Object.keys(dbInvoices)))
        }

        // Return combined results
        return { ...cachedInvoices, ...dbInvoices }
    }

    /**
     * Update a single invoice
     * Handles all the logic internally - no external reading/writing needed
     */
    async updateInvoice(
        id: string,
        updates: Partial<Invoice>,
    ): Promise<Invoice> {
        const currentInvoices = this._data.invoices
        const existingInvoice = currentInvoices[id]

        if (!existingInvoice) {
            throw new Error(`Invoice with id ${id} not found in cache`)
        }

        const updatedInvoice = {
            ...existingInvoice,
            ...updates,
        }

        this._data.invoices = {
            ...currentInvoices,
            [id]: updatedInvoice,
        }

        this.onUpdate.emit(new Set(['invoices']))
        this.onInvoicesUpdate.emit(new Set([id]))
        return updatedInvoice
    }

    /**
     * Set or update emails
     * Accepts an array of complete or partial email objects
     * For partial objects, missing data is pulled from existing cache entries
     */
    async setEmail(emails: (Email | Partial<Email>)[]): Promise<Email[]> {
        const currentEmails = this._data.emails
        const updatedEmails = { ...currentEmails }

        const result: Email[] = []
        const changedIds = new Set<string>()
        const addedIds = new Set<string>()

        for (const email of emails) {
            if (!email) {
                continue
            }

            const id = email.id
            if (!id) {
                throw new Error('Each email must have an id property')
            }
            const existingEmail = currentEmails[id]

            let finalEmail: Email

            if (existingEmail) {
                // Merge partial update with existing email
                finalEmail = { ...existingEmail, ...email }
            } else {
                // New email - must be complete
                finalEmail = email as Email
            }

            // Only mark as changed if the content actually differs
            const was = currentEmails[id]
            const isDifferent =
                was == null ||
                Object.keys({ ...was, ...finalEmail }).some((k) => {
                    const key = k as keyof Email
                    return was[key] !== finalEmail[key]
                })

            updatedEmails[id] = finalEmail
            if (isDifferent) {
                changedIds.add(id)
            }
            if (!existingEmail) {
                addedIds.add(id)
            }
            result.push(finalEmail)
        }

        this._data.emails = updatedEmails
        // Maintain membership tracking and notify only on membership changes
        if (addedIds.size > 0) {
            for (const id of addedIds) {
                this.emailIdSet.add(id)
            }
            this.emailIds = Array.from(this.emailIdSet)
            this.onEmailIdsUpdate.emit(addedIds)
            this.onUpdate.emit(new Set(['emails']))
        }
        if (changedIds.size > 0) {
            this.emailsUpdateVersion++
            this.onEmailsUpdate.emit(changedIds)
        }

        return result
    }

    /**
     * Set or update invoices
     * Accepts an array of complete or partial invoice objects
     * For partial objects, missing data is pulled from existing cache entries
     */
    async setInvoice(
        invoices: (Invoice | Partial<Invoice>)[],
    ): Promise<Invoice[]> {
        const currentInvoices = this._data.invoices
        const updatedInvoices = { ...currentInvoices }
        const result: Invoice[] = []
        const changedIds = new Set<string>()

        for (const invoice of invoices) {
            if (!('id' in invoice) || !invoice.id) {
                throw new Error('Each invoice must have an id property')
            }

            const id = invoice.id
            const existingInvoice = currentInvoices[id]

            let finalInvoice: Invoice

            if (existingInvoice) {
                // Merge partial update with existing invoice
                finalInvoice = { ...existingInvoice, ...invoice }
            } else {
                // New invoice - must be complete
                finalInvoice = invoice as Invoice
            }

            const was = currentInvoices[id]
            const isDifferent =
                was == null ||
                Object.keys({ ...was, ...finalInvoice }).some((k) => {
                    const key = k as keyof Invoice
                    return was[key] !== finalInvoice[key]
                })

            updatedInvoices[id] = finalInvoice
            if (isDifferent) {
                changedIds.add(id)
            }
            result.push(finalInvoice)
        }

        this._data.invoices = updatedInvoices

        // Update stats with new invoice count
        this._data.stats = {
            ...this._data.stats,
            invoicesCount: Object.keys(updatedInvoices).length,
        }

        this.onUpdate.emit(new Set(['invoices', 'stats']))
        if (changedIds.size > 0) {
            this.onInvoicesUpdate.emit(changedIds)
        }

        return result
    }

    /**
     * Set or update emailInvoices mappings
     * Accepts an array of email-to-invoice mappings to update
     * Each update replaces the invoice IDs for the specified email
     */
    async setEmailInvoices(
        updates: Array<{ emailId: string; invoiceIds: string[] }>,
    ): Promise<Record<string, string[]>> {
        const updatedEmailInvoices = {
            ...this._data.emailInvoices,
        }

        for (const update of updates) {
            updatedEmailInvoices[update.emailId] = [...update.invoiceIds]
        }

        this._data.emailInvoices = updatedEmailInvoices
        const changedEmailIds = new Set(updates.map((u) => u.emailId))
        this.onUpdate.emit(new Set(['emailInvoices']))
        if (changedEmailIds.size > 0) {
            this.onEmailInvoicesUpdate.emit(changedEmailIds)
        }

        return updatedEmailInvoices
    }

    async updateSettings(updates: Partial<UserSettings>) {
        this._data.settings = {
            ...this._data.settings,
            ...updates,
        } as UserSettings
        this.onUpdate.emit(new Set(['settings']))
    }

    updateAuthState(updates: Partial<FirebaseAuthState>) {
        this._data.authState = {
            ...this._data.authState,
            ...updates,
        } as FirebaseAuthState
        this.onUpdate.emit(new Set(['authState']))
    }

    // IDs-only accessor for React components that only care about membership
    getEmailIds(): string[] {
        if (
            this.emailIdSet.size === 0 &&
            Object.keys(this._data.emails).length > 0
        ) {
            this.emailIdSet = new Set(Object.keys(this._data.emails))
            this.emailIds = Array.from(this.emailIdSet)
        }
        return this.emailIds
    }

    getEmailsUpdateVersion(): number {
        return this.emailsUpdateVersion
    }

    async setTransaction(
        transactions: (BankTransaction | Partial<BankTransaction>)[],
    ): Promise<BankTransaction[]> {
        const currentTransactions = this._data.transactions ?? {}
        const updatedTransactions = { ...currentTransactions }
        const result: BankTransaction[] = []
        const changedIds = new Set<string>()

        for (const transaction of transactions) {
            if (!('id' in transaction) || !transaction.id) {
                throw new Error('Each transaction must have an id property')
            }

            const id = transaction.id
            const existingTransaction = currentTransactions[id]

            let finalTransaction: BankTransaction

            if (existingTransaction) {
                // Merge partial update with existing transaction
                finalTransaction = { ...existingTransaction, ...transaction }
            } else {
                // New transaction - must be complete
                finalTransaction = transaction as BankTransaction
            }

            const was = currentTransactions[id]
            const isDifferent =
                was == null ||
                Object.keys({ ...was, ...finalTransaction }).some((k) => {
                    const key = k as keyof BankTransaction
                    return was[key] !== finalTransaction[key]
                })

            updatedTransactions[id] = finalTransaction
            if (isDifferent) {
                changedIds.add(id)
            }
            result.push(finalTransaction)
        }

        this._data.transactions = updatedTransactions

        this.onUpdate.emit(new Set(['transactions']))
        if (changedIds.size > 0) {
            this.onTransactionsUpdate.emit(changedIds)
        }

        return result
    }

    async setBankAccounts(
        accounts: (UserBankAccount | Partial<UserBankAccount>)[],
    ): Promise<UserBankAccount[]> {
        const currentBankAccounts = this._data.bankAccounts ?? {}
        const updatedBankAccounts = { ...currentBankAccounts }
        const result: UserBankAccount[] = []
        const changedIds = new Set<string>()

        for (const account of accounts) {
            if (!('iban ' in account) || !account.iban) {
                throw new Error('Each account must have an id property')
            }

            const id = account.iban
            const existingAccount = currentBankAccounts[id]

            let finalAccount: UserBankAccount

            if (existingAccount) {
                // Merge partial update
                finalAccount = { ...existingAccount, ...account }
            } else {
                // New account - must be complete
                finalAccount = account as UserBankAccount
            }

            const was = currentBankAccounts[id]
            const isDifferent =
                was == null ||
                JSON.stringify(was) !== JSON.stringify(finalAccount)

            updatedBankAccounts[id] = finalAccount
            if (isDifferent) {
                changedIds.add(id)
            }
            result.push(finalAccount)
        }

        this._data.bankAccounts = updatedBankAccounts

        this.onUpdate.emit(new Set(['bankAccounts']))
        if (changedIds.size > 0) {
            // Emit fine-grained if you add an emitter later
        }

        return result
    }

    // Partial update helpers for atomic changes
    private updatePartialDocumentAssociations(
        addedOrModified: DocumentTransactionAssociation[],
        removed: string[],
    ) {
        const changedIds = new Set<string>()
        for (const assoc of addedOrModified) {
            this._data.documentTransactionAssociations[assoc.id] = assoc
            changedIds.add(assoc.id)
        }
        for (const id of removed) {
            delete this._data.documentTransactionAssociations[id]
            changedIds.add(id)
        }
        // Emit granular for associations
        if (changedIds.size > 0) {
            this.onMatchingAssociationsUpdate.emit(changedIds)
        }
        return changedIds
    }

    private updatePartialMatchesByTransaction(
        addedOrModified: DocumentTransactionAssociation[],
        removed: string[],
        prevAssociations: Record<string, DocumentTransactionAssociation>, // For removed lookups
    ) {
        const affectedTransactions = new Set<string>()
        const currentMap = { ...this._data.matchesByTransaction } // Shallow copy for immutability

        // Handle added/modified: update specific transaction's array
        for (const assoc of addedOrModified) {
            const txId = assoc.transactionId
            affectedTransactions.add(txId)
            let txArray = currentMap[txId]
            if (!txArray) {
                txArray = []
                currentMap[txId] = txArray
            }
            // Atomic: replace only if needed (e.g., add if new, or ensure ID is present for modified)
            if (!txArray.includes(assoc.id)) {
                txArray.push(assoc.id)
            }
        }

        // Handle removed: update specific transaction's array
        for (const id of removed) {
            const prevAssoc = prevAssociations[id]
            if (prevAssoc) {
                const txId = prevAssoc.transactionId
                affectedTransactions.add(txId)
                let txArray = currentMap[txId]
                if (txArray) {
                    const updatedArray = txArray.filter((aId) => aId !== id)
                    if (updatedArray.length === 0) {
                        delete currentMap[txId]
                    } else {
                        currentMap[txId] = updatedArray // Replace only this array
                    }
                }
            }
        }

        // Apply: shallow assign the whole map (but only affected entries changed)
        this._data.matchesByTransaction = currentMap
        if (affectedTransactions.size > 0) {
            this.onTransactionsWithAssociationChanges.emit(affectedTransactions)
        }
        return affectedTransactions
    }

    private updatePartialMatchesByInvoice(
        addedOrModified: DocumentTransactionAssociation[],
        removed: string[],
        prevAssociations: Record<string, DocumentTransactionAssociation>,
    ) {
        const affectedInvoices = new Set<string>()
        const currentMap = { ...this._data.matchesByInvoice } // Shallow copy

        // Similar to above, but for documentId (invoices)
        for (const assoc of addedOrModified) {
            const invId = assoc.documentId
            affectedInvoices.add(invId)
            let invArray = currentMap[invId]
            if (!invArray) {
                invArray = []
                currentMap[invId] = invArray
            }
            if (!invArray.includes(assoc.id)) {
                invArray.push(assoc.id)
            }
        }

        for (const id of removed) {
            const prevAssoc = prevAssociations[id]
            if (prevAssoc) {
                const invId = prevAssoc.documentId
                affectedInvoices.add(invId)
                let invArray = currentMap[invId]
                if (invArray) {
                    const updatedArray = invArray.filter((aId) => aId !== id)
                    if (updatedArray.length === 0) {
                        delete currentMap[invId]
                    } else {
                        currentMap[invId] = updatedArray
                    }
                }
            }
        }

        this._data.matchesByInvoice = currentMap
        if (affectedInvoices.size > 0) {
            this.onInvoicesWithAssociationChanges.emit(affectedInvoices)
        }
        return affectedInvoices
    }

    // Process queued deltas atomically (called by throttled listener)
    private processAssociationsDelta(
        added: DocumentTransactionAssociation[],
        modified: DocumentTransactionAssociation[],
        removed: string[],
    ) {
        const allAddedModified = [...added, ...modified]
        const uniqueAddedModified = [
            ...new Map(allAddedModified.map((a) => [a.id, a])).values(),
        ]
        const uniqueRemoved = [...new Set(removed)]

        const prevAssociations = {
            ...this._data.documentTransactionAssociations,
        }

        // Atomic updates via helpers
        this.updatePartialDocumentAssociations(
            uniqueAddedModified,
            uniqueRemoved,
        )
        this.updatePartialMatchesByTransaction(
            uniqueAddedModified,
            uniqueRemoved,
            prevAssociations,
        )
        this.updatePartialMatchesByInvoice(
            uniqueAddedModified,
            uniqueRemoved,
            prevAssociations,
        )

        // Emit events
        if (uniqueAddedModified.length > 0 || uniqueRemoved.length > 0) {
            this.onUpdate.emit(
                new Set([
                    'matchesByTransaction',
                    'matchesByInvoice',
                    'documentTransactionAssociations',
                ]),
            )
            this.onMatchingAssociationsUpdate.emit(
                new Set(
                    uniqueAddedModified.map((a) => a.id).concat(uniqueRemoved),
                ),
            )
            // Granular emits
            const affectedTransactions = new Set<string>()
            const affectedInvoices = new Set<string>()
            for (const assoc of uniqueAddedModified) {
                affectedTransactions.add(assoc.transactionId)
                affectedInvoices.add(assoc.documentId)
            }
            if (affectedTransactions.size > 0) {
                this.onTransactionsWithAssociationChanges.emit(
                    affectedTransactions,
                )
            }
            if (affectedInvoices.size > 0) {
                this.onInvoicesWithAssociationChanges.emit(affectedInvoices)
            }
        }
    }

    async setDocumentTransactionAssociations(
        associations: (
            | DocumentTransactionAssociation
            | Partial<DocumentTransactionAssociation>
        )[],
    ): Promise<DocumentTransactionAssociation[]> {
        const currentAssociations =
            this._data.documentTransactionAssociations ?? {}
        const updatedAssociations = { ...currentAssociations }
        const result: DocumentTransactionAssociation[] = []
        const added: DocumentTransactionAssociation[] = []
        const modified: DocumentTransactionAssociation[] = []

        for (const assocInput of associations) {
            if (
                typeof assocInput !== 'object' ||
                assocInput === null ||
                !('id' in assocInput) ||
                !assocInput.id
            ) {
                throw new Error('Each association must have an id property')
            }

            const id = assocInput.id as string
            const existing = currentAssociations[id]

            let finalAssoc: DocumentTransactionAssociation

            if (existing) {
                finalAssoc = {
                    ...existing,
                    ...assocInput,
                } as DocumentTransactionAssociation
                const isDifferent = Object.keys(finalAssoc).some((key) => {
                    const k = key as keyof DocumentTransactionAssociation
                    return existing[k] !== finalAssoc[k]
                })
                if (isDifferent) {
                    modified.push(finalAssoc)
                }
                updatedAssociations[id] = finalAssoc
            } else {
                finalAssoc = assocInput as DocumentTransactionAssociation
                added.push(finalAssoc)
                updatedAssociations[id] = finalAssoc
            }

            result.push(finalAssoc)
        }

        this._data.documentTransactionAssociations = updatedAssociations

        if (added.length > 0 || modified.length > 0) {
            const allAddedModified = [...added, ...modified]
            const uniqueAddedModified = [
                ...new Map(allAddedModified.map((a) => [a.id, a])).values(),
            ]
            this.processAssociationsDelta(uniqueAddedModified, [], [])
        }

        return result
    }

    removeDocumentTransactionAssociations(
        ids: string[],
    ): DocumentTransactionAssociation[] {
        const uniqueIds = [...new Set(ids)]
        const current = {
            ...(this._data.documentTransactionAssociations ?? {}),
        }
        const removed: string[] = uniqueIds.filter((id) => id in current)
        const removedAssocs: DocumentTransactionAssociation[] = removed.map(
            (id) => current[id],
        )

        if (removed.length === 0) {
            return []
        }

        this.processAssociationsDelta([], [], removed)

        return removedAssocs
    }
    updateSearchState(updates: Partial<CachedData['searchState']>) {
        this._data.searchState = {
            ...this._data.searchState,
            ...updates,
        } as CachedData['searchState']

        localStorage.setItem(
            'searchState',
            JSON.stringify(this._data.searchState),
        )

        // Update URL query string
        updateQueryStringWithSearchState(this._data.searchState)
    }

    // Realtime listeners moved to app/src/services/realtime-sync.ts
}
